import { MediaImage } from "@/components/admin/MediaAsset";
import { useState, useRef, useEffect } from "react";
import { Plus, Pencil, Trash2, Database, Plug, Loader2, MapPin, Upload, X, Images } from "lucide-react";
import { toast } from "sonner";
import FavoriteHashtags from "@/components/admin/FavoriteHashtags";
import api, { apiError } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import WidgetPreview from "@/components/admin/WidgetPreview";
import AIConfigForm from "@/components/admin/AIConfigForm";
import SocialConfigForm from "@/components/admin/SocialConfigForm";
import MediaGallery from "@/components/admin/MediaGallery";
import DatabaseConnectionFields from "@/components/admin/DatabaseConnectionFields";
import { businessConnectionPayload, mongoConnectionForm, mongoUriFromFields } from "@/lib/mongoConnection";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  name: "",
  description: "",
  db_type: "mysql",
  schema_mode: "standard",
  use_demo: true,
  db_config: { host: "", port: "", username: "", password: "", database: "", uri: "" },
  widget_position: "bottom-right",
  brand_color: "#2563EB",
  greeting: "Halo! Ada yang bisa kami bantu hari ini?",
  fonnte_token: "",
  admin_whatsapp: "",
  quick_replies: ["Lihat daftar produk", "Berapa harganya?", "Cara pemesanan", "Hubungi admin"],
  logo_url: "",
  address: "",
  operating_hours: "",
  email: "",
  sosmed: "",
  replace_db_config: false,
  openai_api_key: "",
  clear_openai_key: false,
  custom_tables: "",
  ai_model: "",
  gemini_api_key: "",
  clear_gemini_key: false,
};

export default function Businesses({ businesses, loading, reload, setActiveId, editKeyBizId, onEditKeyHandled }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [mongoMode, setMongoMode] = useState("fields");
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [savedOpenAIMask, setSavedOpenAIMask] = useState("");
  const [highlightKey, setHighlightKey] = useState(false);
  const keySectionRef = useRef(null);
  const keyTimers = useRef([]);
  useEffect(() => () => keyTimers.current.forEach(clearTimeout), []);
  const [uploading, setUploading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const fileRef = useRef(null);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${r.data.path}`;
      setForm((f) => ({ ...f, logo_url: url }));
      toast.success("Logo terunggah");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openNew = () => { setForm(EMPTY); setSavedOpenAIMask(""); setMongoMode("fields"); setEditId(null); setOpen(true); };
  const openEdit = (b) => {
    const config = { ...EMPTY.db_config, ...(b.db_config || {}) };
    const connection = b.db_type === "mongodb" ? (config.has_uri ? { mode: "uri", config } : mongoConnectionForm(config)) : { mode: "fields", config };
    setForm({ ...EMPTY, ...b, gemini_api_key: "", gemini_api_key_masked: b.gemini_api_key, clear_gemini_key: false, openai_api_key: "", clear_openai_key: false, db_config: connection.config });
    setSavedOpenAIMask(b.has_openai_key ? b.openai_api_key : "");
    setMongoMode(connection.mode);
    setEditId(b.id);
    setOpen(true);
  };

  useEffect(() => {
    if (!editKeyBizId) return;
    const business = businesses.find((item) => item.id === editKeyBizId);
    if (!business) return;
    openEdit(business);
    setHighlightKey(true);
    keyTimers.current.push(setTimeout(() => {
      keySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      keySectionRef.current?.querySelector("input")?.focus();
    }, 350));
    keyTimers.current.push(setTimeout(() => setHighlightKey(false), 3500));
    onEditKeyHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editKeyBizId, businesses]);

  const save = async (testConnection = false) => {
    if (!form.name.trim()) { toast.error("Nama bisnis wajib diisi"); return; }
    if (form.schema_mode === "custom" && !form.use_demo) {
      const names = form.custom_tables.split(/[,\r\n]+/).map((name) => name.trim()).filter(Boolean);
      if (!names.length || names.some((name) => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(name))) {
        toast.error("Isi nama tabel/koleksi Custom dengan huruf, angka dan underscore."); return;
      }
      if (new Set(names).size > 20) { toast.error("Maksimal 20 tabel/koleksi Custom per bisnis."); return; }
    }
    if (testConnection === true && form.db_config_unavailable && !form.replace_db_config) {
      toast.error("Pilih opsi ganti koneksi lama, lalu isi konfigurasi baru sebelum melakukan tes.");
      return;
    }
    const payload = { ...businessConnectionPayload(form, mongoMode), business_id: editId };
    setSaving(true);
    try {
      if (testConnection === true) {
        const result = await api.post("/businesses/test-db", payload);
        if (!result.data.ok) { toast.error(result.data.message); return; }
      }
      if (editId) {
        await api.put(`/businesses/${editId}`, payload);
        toast.success(testConnection === true ? "Koneksi berhasil dan bisnis disimpan" : "Bisnis diperbarui");
      } else {
        const r = await api.post("/businesses", payload);
        setActiveId(r.data.id);
        toast.success(testConnection === true ? "Koneksi berhasil dan bisnis ditambahkan" : "Bisnis ditambahkan");
      }
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus bisnis ini beserta lead-nya?")) return;
    await api.delete(`/businesses/${id}`);
    toast.success("Bisnis dihapus");
    reload();
  };

  const testDb = async (id) => {
    setTestingId(id);
    try {
      const r = await api.post(`/businesses/${id}/test-db`);
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTestingId(null);
    }
  };

  const setCfg = (k, v) => setForm((f) => ({ ...f, db_config: { ...f.db_config, [k]: v } }));
  const changeMongoMode = (mode) => {
    setForm((current) => ({
      ...current,
      db_config: mode === "uri"
        ? { ...current.db_config, uri: current.db_config.has_password ? "" : mongoUriFromFields(current.db_config) }
        : mongoConnectionForm(current.db_config).config,
    }));
    setForm((current) => ({ ...current, db_config: { ...current.db_config, clear_uri: true } }));
    setMongoMode(mode);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-slate-600">Kelola bisnis Anda, masing-masing dengan database & widget sendiri.</p>
        <Button onClick={openNew} data-testid="btn-add-business" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
          <Plus size={16} className="mr-1" /> Tambah Bisnis
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Memuat...</div>
      ) : businesses.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center">
          <Database size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-600 mb-4">Belum ada bisnis. Tambahkan bisnis pertama Anda.</p>
          <Button onClick={openNew} className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
            <Plus size={16} className="mr-1" /> Tambah Bisnis
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {businesses.map((b) => (
            <div key={b.id} data-testid={`business-card-${b.id}`} className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-5 hover:border-[#3B82F6] transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading font-bold text-base">{b.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{b.description || "Tanpa deskripsi"}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(b)} data-testid={`edit-business-${b.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#2563EB] transition-colors"><Pencil size={15} /></button>
                  <button onClick={() => remove(b.id)} data-testid={`delete-business-${b.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#E11D48] transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-[11px] font-mono-code px-2 py-1 rounded-md bg-[#F1F5F9] text-slate-700 uppercase">{b.db_type}</span>
                <span className="text-[11px] font-mono-code px-2 py-1 rounded-md bg-[#F1F5F9] text-slate-700">{b.schema_mode === "custom" ? "Custom" : b.schema_mode === "woocommerce" ? "WooCommerce" : "Standard"}</span>
                {b.use_demo && <span className="text-[11px] font-mono-code px-2 py-1 rounded-md bg-[#10B981]/15 text-[#059669]">DEMO</span>}
                <span className="text-[11px] font-mono-code px-2 py-1 rounded-md bg-[#F1F5F9] text-slate-700 flex items-center gap-1"><MapPin size={10} />{b.widget_position === "bottom-left" ? "Kiri" : "Kanan"}</span>
              </div>
              {b.db_config_unavailable && <p className="mb-3 text-xs text-[#B45309]" data-testid={`connection-unavailable-${b.id}`}>Koneksi lama perlu diisi ulang melalui Edit Bisnis.</p>}
              <Button onClick={() => testDb(b.id)} disabled={testingId === b.id} variant="outline" className="w-full border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-xs" data-testid={`test-db-${b.id}`}>
                {testingId === b.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plug size={14} className="mr-1" />}
                Tes Koneksi Database
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-3xl max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Bisnis" : "Tambah Bisnis"}</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Lengkapi profil bisnis — info statis (alamat, jam, email, sosmed) dijawab chatbot tanpa memanggil AI.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 space-y-4">
            {form.db_config_unavailable && (
              <div role="alert" data-testid="connection-recovery-panel" className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-3 text-xs text-slate-700">
                <p>Koneksi lama tidak dapat dibaca. Profil bisnis tetap bisa disimpan. Isi ulang seluruh konfigurasi koneksi untuk menggunakan database bisnis ini.</p>
                <label className="mt-3 flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.replace_db_config} onChange={(event) => setForm((current) => ({ ...current, replace_db_config: event.target.checked }))} data-testid="replace-db-config-checkbox" className="accent-[#2563EB]" />
                  Ganti koneksi lama dengan konfigurasi yang saya isi
                </label>
              </div>
            )}
            {form.fonnte_token_unavailable && <p role="alert" className="text-xs text-[#B45309]">Token WhatsApp lama perlu diisi ulang melalui Settings. Profil bisnis tetap bisa disimpan.</p>}
            <div>
              <Label className="text-xs text-slate-600">Nama Bisnis</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="business-name-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Toko Kopi Nusantara" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Deskripsi</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" rows={2} placeholder="Toko kopi specialty..." />
            </div>

            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><MapPin size={12} /> Alamat Kantor / Toko</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="business-address-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Jl. Sudirman No. 123, Jakarta Selatan" />
            </div>
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1">Jam Operasional</Label>
              <Input value={form.operating_hours} onChange={(e) => setForm({ ...form, operating_hours: e.target.value })} data-testid="business-hours-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Senin–Sabtu, 08.00–20.00 WIB" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Email Bisnis</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="business-email-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="halo@toko.com" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Media Sosial</Label>
                <Input value={form.sosmed} onChange={(e) => setForm({ ...form, sosmed: e.target.value })} data-testid="business-sosmed-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="@tokokopi (IG/TikTok)" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">Chatbot otomatis menjawab pertanyaan alamat, jam buka, email & media sosial dari kolom-kolom ini (tanpa AI).</p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Tipe Database</Label>
                <Select value={form.db_type} onValueChange={(v) => setForm({ ...form, db_type: v })}>
                  <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="db-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                    <SelectItem value="mysql">MySQL</SelectItem>
                    <SelectItem value="postgresql">PostgreSQL</SelectItem>
                    <SelectItem value="mongodb">MongoDB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Struktur Tabel</Label>
                <Select value={form.schema_mode} onValueChange={(v) => setForm({ ...form, schema_mode: v })}>
                  <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="schema-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                    <SelectItem value="standard">Standard (products/orders)</SelectItem>
                    <SelectItem value="woocommerce">WooCommerce (wp_posts)</SelectItem>
                    <SelectItem value="custom">Custom (nama tabel sendiri)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.schema_mode === "custom" && <div>
              <Label className="text-xs text-slate-600">Nama Tabel / Koleksi Custom</Label>
              <Textarea value={form.custom_tables} onChange={(event) => setForm({ ...form, custom_tables: event.target.value })} data-testid="custom-tables-input" className="mt-1 bg-[#F1F5F9] border-[#E2E8F0]" placeholder={"catalog_items, sales_orders\nPisahkan nama dengan koma atau baris baru"} />
              <p className="text-[11px] text-slate-500 mt-1">Maksimal 20 nama. Kolom bebas; nama harus diawali huruf atau underscore.</p>
            </div>}

            <div className="flex items-center justify-between bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-medium">Gunakan data demo</p>
                <p className="text-xs text-slate-500">Pakai katalog contoh tanpa DB eksternal</p>
              </div>
              <Switch checked={form.use_demo} onCheckedChange={(v) => setForm({ ...form, use_demo: v })} data-testid="use-demo-switch" />
            </div>

            {!form.use_demo && (
              <DatabaseConnectionFields dbType={form.db_type} config={form.db_config} mode={mongoMode} onModeChange={changeMongoMode} onChange={setCfg} disabled={saving || (form.db_config_unavailable && !form.replace_db_config)} />
            )}

            <div ref={keySectionRef} data-testid="openai-key-section" className={`rounded-xl border p-3 space-y-2 ${highlightKey ? "border-purple-500 ring-2 ring-purple-500/30" : "border-[#E2E8F0]"}`}>
              <AIConfigForm key={editId || "new"} businessId={editId} value={form} onChange={(changes) => setForm((current) => ({ ...current, ...changes }))} masked={savedOpenAIMask} disabled={saving} onSaved={reload} />
              <SocialConfigForm key={`social-${editId || "new"}`} businessId={editId} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Posisi Ikon Chatbot</Label>
                <Select value={form.widget_position} onValueChange={(v) => setForm({ ...form, widget_position: v })}>
                  <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="position-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                    <SelectItem value="bottom-right">Kanan Bawah</SelectItem>
                    <SelectItem value="bottom-left">Kiri Bawah</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Warna Brand</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="w-10 h-10 rounded-lg bg-transparent border border-[#E2E8F0] cursor-pointer" />
                  <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="bg-[#F1F5F9] border-[#E2E8F0] font-mono-code text-xs" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Pesan Sambutan Chatbot</Label>
              <Input value={form.greeting} onChange={(e) => setForm({ ...form, greeting: e.target.value })} className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
            </div>

            <div>
              <Label className="text-xs text-slate-600">Logo Bisnis (unggah file atau tempel URL)</Label>
              <div className="flex gap-2 mt-1 items-center">
                <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="logo-url-input" className="bg-[#F1F5F9] border-[#E2E8F0]" placeholder="https://.../logo.png" />
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
                <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" data-testid="logo-upload-button" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl shrink-0">
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                </Button>
                <Button type="button" onClick={() => setGalleryOpen(true)} variant="outline" data-testid="logo-gallery-button" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl shrink-0" title="Pilih dari Galeri">
                  <Images size={15} />
                </Button>
                {form.logo_url && (
                  <>
                    <MediaImage src={form.logo_url} alt="logo" className="w-9 h-9 rounded-full object-cover border border-[#E2E8F0] shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                    <Button type="button" onClick={() => setForm({ ...form, logo_url: "" })} variant="outline" data-testid="logo-remove-button" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] hover:text-[#E11D48] rounded-xl shrink-0" title="Hapus logo">
                      <X size={15} />
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Balasan Cepat (satu per baris, muncul sebagai tombol di widget)</Label>
              <Textarea
                value={(form.quick_replies || []).join("\n")}
                onChange={(e) => setForm({ ...form, quick_replies: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                rows={3}
                data-testid="quick-replies-input"
                className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 text-xs"
                placeholder={"Lihat daftar produk\nBerapa harganya?\nCara pemesanan"}
              />
            </div>

            {!form.use_demo && (
              <Button onClick={() => save(true)} disabled={saving} data-testid="business-test-save-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
                {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : <Plug size={16} className="mr-1" />}
                Tes &amp; Simpan Koneksi
              </Button>
            )}
            {editId && <FavoriteHashtags key={editId} businessId={editId} />}
            <Button onClick={() => save()} disabled={saving} data-testid="business-modal-save-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
              {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
              {editId ? "Simpan Perubahan" : "Tambah Bisnis"}
            </Button>
          </div>

          <div className="md:col-span-2">
            <div className="md:sticky md:top-0">
              <p className="text-xs text-slate-600 mb-3">Pratinjau Widget</p>
              <WidgetPreview
                name={form.name}
                brandColor={form.brand_color}
                logoUrl={form.logo_url}
                greeting={form.greeting}
                quickReplies={form.quick_replies}
                position={form.widget_position}
              />
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-2xl max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Pilih dari Galeri Media</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Pilih gambar dari galeri media untuk dipakai sebagai logo/foto bisnis.</DialogDescription></DialogHeader>
          <MediaGallery picker onPick={(url) => { setForm((f) => ({ ...f, logo_url: url })); setGalleryOpen(false); toast.success("Logo dipilih dari galeri"); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
